import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Legal Documents — admin list of every LegalDocument version
// (DRAFT / PUBLISHED / ARCHIVED) used by the Large Projects module.
//
// Consumes besthand-api `listLegalDocuments(status)` (returns `legalDocuments`)
// through the `/lp-graphql` proxy via `lpApi`. Admin-gated (requireAdmin).
// ============================================================================

const Q_LEGAL = `
  query ($status: String) {
    listLegalDocuments(status: $status) {
      status errorMessage
      legalDocuments {
        id slug version title status contentHash effectiveDate
        requiresLegalReview publishedAt publishedBy archivedAt createdAt
      }
    }
  }
`;

// publishLegalDocument(legalDocumentId!) — DRAFT → PUBLISHED (archives the
// prior PUBLISHED version of the same slug server-side).
const M_PUBLISH = `
  mutation ($legalDocumentId: String!) {
    publishLegalDocument(legalDocumentId: $legalDocumentId) {
      status errorMessage
      legalDocument { id status publishedAt }
    }
  }
`;

// archiveLegalDocument(legalDocumentId!)
const M_ARCHIVE = `
  mutation ($legalDocumentId: String!) {
    archiveLegalDocument(legalDocumentId: $legalDocumentId) {
      status errorMessage
      legalDocument { id status archivedAt }
    }
  }
`;

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  select: { padding: '9px 12px', border: '1px solid #d8dde2', borderRadius: 9, fontSize: 14, fontWeight: 600, outline: 'none', background: '#fff', cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  warn: { fontSize: 12, fontWeight: 800, color: '#946200', marginTop: 6 },
  badge: (kind) => {
    const map = {
      PUBLISHED: { bg: '#e7f6ec', fg: '#137a37' },
      DRAFT: { bg: '#eef2ff', fg: '#3730a3' },
      ARCHIVED: { bg: '#eef2f4', fg: '#5a6168' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnGhostSm: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function LegalDocs() {
  const [docs, setDocs] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async (statusArg) => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_LEGAL, { status: statusArg || null });
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listLegalDocuments;
        if (env && env.status === 200) setDocs(env.legalDocuments || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load legal documents: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(status); }, [load, status]);

  const run = async (key, query, variables, opName, okText) => {
    setBusyId(key);
    setMsg(null);
    try {
      const res = await lpApi(query, variables);
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data[opName];
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: okText });
        await load(status);
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  const publish = (d) => {
    if (d.requiresLegalReview && typeof window !== 'undefined') {
      const ok = window.confirm(`⚠ "${d.title || d.slug}" v${d.version} is still flagged "Needs legal review".\n\nPublishing makes it the live, binding version (any current published version of this slug is archived). Continue?`);
      if (!ok) return;
    }
    run(`d${d.id}`, M_PUBLISH, { legalDocumentId: String(d.id) }, 'publishLegalDocument', `Published ${d.title || d.slug} v${d.version}.`);
  };

  const archive = (d) => {
    run(`d${d.id}`, M_ARCHIVE, { legalDocumentId: String(d.id) }, 'archiveLegalDocument', `Archived ${d.title || d.slug} v${d.version}.`);
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Legal Documents</h2>
      <div style={S.hint}>
        Large Projects — every legal document version (terms, disclosures, lien-waiver templates) by slug and
        version. A “Needs legal review” flag marks drafts awaiting attorney sign-off.
      </div>

      <div style={S.toolbar}>
        <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button style={S.btnGhost} onClick={() => load(status)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{docs.length} shown</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        docs.length === 0 ? (
          <div style={S.empty}>No legal documents for this filter.</div>
        ) : docs.map((d) => (
          <div style={S.card} key={d.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>{d.title || d.slug} <span style={{ color: '#9ca3af', fontWeight: 600 }}>v{d.version}</span></p>
                <div style={S.meta}>
                  {d.slug}
                  {d.effectiveDate ? ` · Effective ${d.effectiveDate}` : ''}
                  {d.publishedAt ? ` · Published ${fmtDate(d.publishedAt)}` : ''}
                  {d.publishedBy ? ` by ${d.publishedBy}` : ''}
                </div>
                <div style={S.meta}>
                  {d.contentHash ? `Hash ${String(d.contentHash).slice(0, 16)}… · ` : ''}
                  Created {d.createdAt ? fmtDate(d.createdAt) : '—'}
                  {d.archivedAt ? ` · Archived ${fmtDate(d.archivedAt)}` : ''}
                </div>
                {d.requiresLegalReview ? <div style={S.warn}>⚠ Needs legal review</div> : null}
              </div>
              <span style={S.badge(d.status)}>{d.status || '—'}</span>
            </div>
            {(d.status === 'DRAFT' || d.status === 'PUBLISHED') && (
              <div style={S.actions}>
                {d.status === 'DRAFT' && (
                  <button style={S.btn} disabled={busyId === `d${d.id}`} onClick={() => publish(d)}>
                    {busyId === `d${d.id}` ? '…' : 'Publish'}
                  </button>
                )}
                <button style={S.btnGhostSm} disabled={busyId === `d${d.id}`} onClick={() => archive(d)}>
                  {busyId === `d${d.id}` ? '…' : 'Archive'}
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default LegalDocs;
